import React, { useState } from 'react';
import { MoreVertical, BellOff, UserMinus, UserX, BellRing } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { cn } from '../lib/utils';
import { addDays } from 'date-fns';

interface UserVisibilityControlsProps {
  userId: string;
  targetUserId: string;
  onVisibilityChange?: () => void;
}

export default function UserVisibilityControls({ userId, targetUserId, onVisibilityChange }: UserVisibilityControlsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { preference, updatePreference, deletePreference } = useUserPreferences(userId, targetUserId);

  const handleAction = async (action: () => Promise<void>) => {
    try {
      setLoading(true);
      setError(null);
      await action();
      onVisibilityChange?.();
      setIsOpen(false);
    } catch (err: any) {
      console.error('Error performing action:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSnooze = () => handleAction(async () => {
    const snoozedUntil = addDays(new Date(), 30).toISOString();
    await updatePreference({
      snoozed_until: snoozedUntil,
      is_following: false
    });
  });

  const handleUnsnooze = () => handleAction(async () => {
    await deletePreference();
  });

  const handleUnfollow = () => handleAction(async () => {
    await updatePreference({
      snoozed_until: null,
      is_following: false
    });
  });

  const handleRefollow = () => handleAction(async () => {
    await deletePreference();
  });

  const isSnoozed = preference?.snoozed_until && new Date(preference.snoozed_until) > new Date();

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "p-2 rounded-full hover:bg-gray-100 focus:outline-none",
          (isSnoozed || (preference && !preference.is_following)) && "text-blue-500"
        )}
        disabled={loading}
        title={isSnoozed ? "Snoozed" : preference && !preference.is_following ? "Unfollowed" : "Manage visibility"}
      >
        <MoreVertical className="h-5 w-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50">
          {error && (
            <div className="px-4 py-2 text-sm text-red-600 border-b border-gray-100">
              {error}
            </div>
          )}
          <div className="py-1" role="menu">
            {isSnoozed ? (
              <button
                onClick={handleUnsnooze}
                disabled={loading}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <BellRing className="h-4 w-4 mr-2" />
                Unsnooze
              </button>
            ) : (
              <button
                onClick={handleSnooze}
                disabled={loading}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <BellOff className="h-4 w-4 mr-2" />
                Snooze for 30 days
              </button>
            )}
            {preference?.is_following !== false && (
              <button
                onClick={handleUnfollow}
                disabled={loading}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <UserMinus className="h-4 w-4 mr-2" />
                Unfollow
              </button>
            )}
            {preference?.is_following === false && (
              <button
                onClick={handleRefollow}
                disabled={loading}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <UserMinus className="h-4 w-4 mr-2" />
                Re-follow
              </button>
            )}
            <button
              onClick={() => handleAction(async () => {
                const { error } = await supabase
                  .from('friends')
                  .delete()
                  .or(`and(sender_id.eq.${userId},receiver_id.eq.${targetUserId}),and(sender_id.eq.${targetUserId},receiver_id.eq.${userId})`);

                if (error) throw error;
              })}
              disabled={loading}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 border-t border-gray-100"
            >
              <UserX className="h-4 w-4 mr-2" />
              Unfriend
            </button>
          </div>
        </div>
      )}
    </div>
  );
}